import {
    dashboardService,
    folderService,
    investmentService,
    listService,
    memoService,
    pipelineService,
    teamService,
} from '@/services';
import { useFolderStore } from '@/stores/database/entities/01-Folders/store';
import { useInvestmentStore } from '@/stores/database/entities/02-Investments/store';
import { useMemoStore } from '@/stores/database/entities/04-Memos/store';
import { usePipelineStore } from '@/stores/database/entities/05-Pipelines/store';
import { useTeamStore } from '@/stores/database/entities/06-Teams/store';
import { useDashboardStore } from '@/stores/database/entities/99-Relations/01-Dashboards/store';
import { useListStore } from '@/stores/database/entities/99-Relations/03-Lists/store';

import {
    createOptimisticCollectionRestoreTransaction,
    createOptimisticCollectionTrashTransaction,
} from './optimisticRestoreTransaction';
import type {
    OptimisticRestoreStoreConfig,
    OptimisticRestoreTransaction,
    OptimisticTrashTransaction,
    RestorableWorkspaceResource,
} from './optimisticRestoreTransaction';

export {
    createOptimisticCollectionRestoreTransaction,
    createOptimisticCollectionTrashTransaction,
} from './optimisticRestoreTransaction';
export type {
    OptimisticRestoreStoreConfig,
    OptimisticRestoreTransaction,
    OptimisticTrashTransaction,
    RestorableWorkspaceResource,
} from './optimisticRestoreTransaction';

export type RestorableWorkspaceResourceType =
    'dashboard' | 'folder' | 'investment' | 'list' | 'memo' | 'pipeline' | 'team';

const restorableWorkspaceResourceTypes = new Set<string>([
    'dashboard',
    'folder',
    'investment',
    'list',
    'memo',
    'pipeline',
    'team',
]);

export const isRestorableWorkspaceResourceType = (value: string): value is RestorableWorkspaceResourceType =>
    restorableWorkspaceResourceTypes.has(value);

interface OptimisticRestoreStore {
    getState: () => Record<string, unknown>;
    setState: (partial: Record<string, unknown>) => void;
}

const asOptimisticRestoreStore = (store: unknown) => store as OptimisticRestoreStore;

const optimisticRestoreStoreMap: Record<RestorableWorkspaceResourceType, OptimisticRestoreStoreConfig> = {
    dashboard: {
        collectionKey: 'dashboards',
        restoreSelection: true,
        selectedKey: 'selectedDashboard',
        store: asOptimisticRestoreStore(useDashboardStore),
    },
    folder: {
        collectionKey: 'folders',
        selectedKey: 'selectedFolder',
        store: asOptimisticRestoreStore(useFolderStore),
    },
    investment: {
        collectionKey: 'investments',
        selectedKey: 'selectedInvestment',
        store: asOptimisticRestoreStore(useInvestmentStore),
    },
    list: {
        collectionKey: 'lists',
        restoreSelection: true,
        selectedKey: 'selectedList',
        store: asOptimisticRestoreStore(useListStore),
    },
    memo: {
        collectionKey: 'memos',
        selectedKey: 'selectedMemo',
        store: asOptimisticRestoreStore(useMemoStore),
    },
    pipeline: {
        collectionKey: 'pipelines',
        selectedKey: 'selectedPipeline',
        store: asOptimisticRestoreStore(usePipelineStore),
    },
    team: {
        collectionKey: 'teams',
        selectedKey: 'selectedTeam',
        store: asOptimisticRestoreStore(useTeamStore),
    },
};

const trashServiceMap: Record<RestorableWorkspaceResourceType, (id: string) => Promise<unknown>> = {
    dashboard: dashboardService.delete,
    folder: folderService.delete,
    investment: investmentService.delete,
    list: listService.delete,
    memo: memoService.delete,
    pipeline: pipelineService.delete,
    team: teamService.delete,
};

export const optimisticallyRestoreWorkspaceResource = (
    resourceType: RestorableWorkspaceResourceType,
    resource: RestorableWorkspaceResource,
): OptimisticRestoreTransaction =>
    createOptimisticCollectionRestoreTransaction(optimisticRestoreStoreMap[resourceType], resource);

/**
 * Uses one optimistic transaction for both individual and bulk Trash actions.
 * A failed request restores only its own row; a successful request re-commits
 * after the response so stale list requests cannot resurrect the resource.
 */
export const moveWorkspaceResourceToTrash = async (
    resourceType: RestorableWorkspaceResourceType,
    resourceId: string,
): Promise<OptimisticTrashTransaction> => {
    const transaction = createOptimisticCollectionTrashTransaction(optimisticRestoreStoreMap[resourceType], resourceId);

    try {
        await trashServiceMap[resourceType](resourceId);
        transaction.commit();

        return transaction;
    } catch (error) {
        transaction.rollback();
        throw error;
    }
};
